'use client';

import { useState, useEffect, useCallback } from 'react';
import type { Announcement, AnnouncementConfig, AnnouncementState } from '@/types/app/announcements';

const COOKIE_NAME = 'announcement_state';
const COOKIE_EXPIRY_DAYS = 365;

export function useAnnouncements() {
  const [announcements, setAnnouncements] = useState<Announcement[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [announcementState, setAnnouncementState] = useState<AnnouncementState>({
    dismissedAnnouncements: [],
    readAnnouncements: [],
    lastChecked: new Date().toISOString()
  });

  // Load announcement state from cookies
  const loadAnnouncementState = useCallback((): AnnouncementState => {
    if (typeof window === 'undefined') {
      return {
        dismissedAnnouncements: [],
        readAnnouncements: [],
        lastChecked: new Date().toISOString()
      };
    }

    try {
      const cookieValue = document.cookie
        .split('; ')
        .find(row => row.startsWith(`${COOKIE_NAME}=`))
        ?.split('=')[1];

      if (cookieValue) {
        const decoded = decodeURIComponent(cookieValue);
        return JSON.parse(decoded);
      }
    } catch (error) {
      console.warn('Failed to parse announcement state from cookie:', error);
    }

    return {
      dismissedAnnouncements: [],
      readAnnouncements: [],
      lastChecked: new Date().toISOString()
    };
  }, []);

  // Save announcement state to cookies
  const saveAnnouncementState = useCallback((state: AnnouncementState) => {
    if (typeof window === 'undefined') return;

    try {
      const encoded = encodeURIComponent(JSON.stringify(state));
      const expiryDate = new Date();
      expiryDate.setDate(expiryDate.getDate() + COOKIE_EXPIRY_DAYS);
      
      document.cookie = `${COOKIE_NAME}=${encoded}; expires=${expiryDate.toUTCString()}; path=/; SameSite=Lax`;
      setAnnouncementState(state);
    } catch (error) {
      console.error('Failed to save announcement state to cookie:', error);
    }
  }, []);

  // Load announcements from authenticated API
  useEffect(() => {
    const loadAnnouncements = async () => {
      try {
        setLoading(true);
        const response = await fetch('/api/announcements', {
          cache: 'no-cache',
          headers: {
            'Cache-Control': 'no-cache'
          }
        });
        
        if (!response.ok) {
          if (response.status === 401) {
            // User not authenticated, no announcements to show
            setAnnouncements([]);
            return;
          }
          throw new Error(`Failed to load announcements: ${response.status}`);
        }

        const config: AnnouncementConfig = await response.json();
        
        if (!config.settings.enabled) {
          setAnnouncements([]);
          return;
        }

        // Filter announcements based on expiry and other criteria
        const now = new Date();
        const validAnnouncements = config.announcements.filter(announcement => {
          if (announcement.expiresAt && new Date(announcement.expiresAt) < now) {
            return false;
          }
          return true;
        });

        setAnnouncements(validAnnouncements);
      } catch (err) {
        console.error('Error loading announcements:', err);
        setError(err instanceof Error ? err.message : 'Failed to load announcements');
      } finally {
        setLoading(false);
      }
    };

    const state = loadAnnouncementState();
    setAnnouncementState(state);
    loadAnnouncements();
  }, [loadAnnouncementState]);

  // Get announcements that should be shown
  const getVisibleAnnouncements = useCallback((): Announcement[] => {
    return announcements.filter(announcement => {
      // Don't show dismissed announcements
      if (announcementState.dismissedAnnouncements.includes(announcement.id)) {
        return false;
      }

      // For showOnce announcements, don't show if already read
      if (announcement.showOnce && announcementState.readAnnouncements.includes(announcement.id)) {
        return false;
      }

      return true;
    }).sort((a, b) => {
      // Sort by priority (critical > high > medium > low)
      const priorityOrder = { critical: 4, high: 3, medium: 2, low: 1 };
      return priorityOrder[b.priority] - priorityOrder[a.priority];
    });
  }, [announcements, announcementState]);

  // Mark announcement as read
  const markAsRead = useCallback((announcementId: string) => {
    const newState = {
      ...announcementState,
      readAnnouncements: [...announcementState.readAnnouncements, announcementId],
      lastChecked: new Date().toISOString()
    };
    saveAnnouncementState(newState);
  }, [announcementState, saveAnnouncementState]);

  // Dismiss announcement
  const dismissAnnouncement = useCallback((announcementId: string) => {
    const newState = {
      ...announcementState,
      dismissedAnnouncements: [...announcementState.dismissedAnnouncements, announcementId],
      lastChecked: new Date().toISOString()
    };
    saveAnnouncementState(newState);
  }, [announcementState, saveAnnouncementState]);

  // Check if announcement has been read
  const isAnnouncementRead = useCallback((announcementId: string): boolean => {
    return announcementState.readAnnouncements.includes(announcementId);
  }, [announcementState]);

  return {
    announcements: getVisibleAnnouncements(),
    loading,
    error,
    markAsRead,
    dismissAnnouncement,
    isAnnouncementRead,
    announcementState
  };
} 